import { TextButton, TextButtonLink } from '@creditornot/cb-button';
import { useMutation } from '@tanstack/react-query';
import { splitEvery } from 'ramda';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { updateItem, uploadMenuImage } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { getErrorContent } from 'common/utils';
import { cleanFileName } from 'modules/menu/hooks/useProcessMenuImage';

import CustomToast from '../components/CustomToast';
import { FileMatch } from '../types';
import { buildItemUpdatePayload } from '../utils/utils';

const MAX_CONCURRENT_IMAGE_UPLOADS = 5;
const TOAST_ID = 'bulk-image-upload-toast';

type UploadError = { fileName: string; error?: string };

const StyledTextButton = styled(TextButton)`
  margin-inline-start: 4px;
  font-weight: 600;
`;

const StyledTextButtonLink = styled(TextButtonLink)`
  margin-inline-start: 4px;
  font-weight: 600;
`;

const incrementUploadProgressToast = (count: number, total: number) => {
  toast.custom(
    <CustomToast variant="loading">
      <FormattedMessage id="menu.bulk-image.upload-progress" values={{ count, total }} />
    </CustomToast>,
    { duration: Infinity, id: TOAST_ID },
  );
};

const uploadAndUpdate = async (fileMatch: FileMatch, menuId: string) => {
  const payload = new File([fileMatch.file], cleanFileName(fileMatch.file.name));
  const imageUrl = await uploadMenuImage(menuId, payload, { called_from_bulk_upload_tool: true });
  const itemUpdatePayloads = (fileMatch.matchedItems || []).map(item => buildItemUpdatePayload(item, imageUrl));
  await Promise.all(itemUpdatePayloads.map(payload => updateItem(payload.menu_id, payload.id, payload)));
};

const useBulkImageUpload = (fileMatches: FileMatch[], venue: Venue, errorCallback: (errors: UploadError[]) => void) => {
  const { menu: menuId, slug: venueSlug } = venue;
  const history = useHistory();

  const successes = useMemo(() => fileMatches.filter(r => r.status === 'success'), [fileMatches]);

  const uploadImages = useCallback(
    async (successMatches: FileMatch[]) => {
      if (!menuId) {
        throw new Error('No menu id in context! This should not happen.');
      }
      const errors: UploadError[] = [];

      const chunk = splitEvery(MAX_CONCURRENT_IMAGE_UPLOADS, successMatches);

      let cursor = 0;
      const total = successMatches.length;
      for (const chunkItems of chunk) {
        await Promise.all(
          chunkItems.map(async successMatch => {
            try {
              await uploadAndUpdate(successMatch, menuId);
            } catch (error) {
              errors.push({ fileName: successMatch.file.name, error: getErrorContent(error) });
            } finally {
              cursor++;
              incrementUploadProgressToast(cursor, total);
            }
          }),
        );
      }
      // Short delay effect for clearer toast feedback
      await new Promise(res => setTimeout(res, 500));

      if (errors.length > 0) {
        errorCallback(errors);
        throw new Error();
      }
    },
    [errorCallback, menuId],
  );

  const handleErrorState = useCallback(
    (isBulkImageUploadPath: boolean) => {
      toast.custom(
        <CustomToast
          variant="error"
          onCloseButtonClick={isBulkImageUploadPath ? () => toast.dismiss(TOAST_ID) : undefined}
        >
          <FormattedMessage id="menu.bulk-image.upload-toast-error" />
          {!isBulkImageUploadPath && (
            <StyledTextButton
              onClick={() => {
                toast.dismiss(TOAST_ID);
                history.push(`/venue/${venueSlug}/bulk-image-upload`);
              }}
            >
              <FormattedMessage id="menu.bulk-image.toast-click-here" />
            </StyledTextButton>
          )}
        </CustomToast>,
        {
          duration: Infinity,
          id: TOAST_ID,
        },
      );
    },
    [history, venueSlug],
  );

  const handleSuccessState = useCallback(
    (successCount: number, isBulkImageUploadPath: boolean) => {
      toast.custom(
        <CustomToast
          variant="success"
          onCloseButtonClick={isBulkImageUploadPath ? () => toast.dismiss(TOAST_ID) : undefined}
        >
          <FormattedMessage id="menu.bulk-image.upload-toast-success" values={{ count: successCount }} />
          {!isBulkImageUploadPath && (
            <StyledTextButtonLink href={`/venue/${venueSlug}/menu-editor`}>
              <FormattedMessage id="menu.bulk-image.toast-reload-menu" />
            </StyledTextButtonLink>
          )}
        </CustomToast>,
        {
          duration: Infinity,
          id: TOAST_ID,
        },
      );
    },
    [venueSlug],
  );

  const {
    mutate: handleBulkImageUpload,
    status: uploadStatus,
    reset: resetUploadState,
  } = useMutation(
    async () => {
      if (!menuId) {
        throw new Error('No menu id in context! This should not happen.');
      }
      toast.custom(<CustomToast variant="loading">...</CustomToast>, { duration: Infinity, id: TOAST_ID });

      await uploadImages(successes);
    },
    {
      onError: () => {
        const isBulkImageUploadPath = window.location.pathname.includes('/bulk-image-upload');
        handleErrorState(isBulkImageUploadPath);
      },
      onSuccess: () => {
        const isBulkImageUploadPath = window.location.pathname.includes('/bulk-image-upload');
        handleSuccessState(successes.length, isBulkImageUploadPath);
      },
    },
  );

  return { handleBulkImageUpload, uploadStatus, resetUploadState };
};

export default useBulkImageUpload;
